import { useState, useCallback, useMemo, useEffect } from 'react';

const STORAGE_PREFIX = 'home_choices_draft_';

/**
 * Hook managing buffered editing for Home Choices.
 *
 * All option and cost changes are buffered locally instead of writing to DB.
 * The hook computes per-item deltas and aggregate totals so the UI can show
 * real-time budget impact. Changes are applied in batch via the parent's save flow.
 *
 * @param {Object} options
 * @param {string} options.projectId - Current project ID (used as sessionStorage key)
 * @param {Array}  options.tableData - Home Choices table rows (from HomeChoicesSection)
 * @param {Array}  options.projectItems - All project_items for this project
 * @param {number} options.totalBudget - Total project budget (for percentage calc)
 */
export function useSimulation({ projectId, tableData, projectItems, totalBudget }) {
  // draftChanges: { [master_item_id]: { selectedOption?, cost?, costManuallySet?, originalOption, originalCost } }
  const [draftChanges, setDraftChanges] = useState({});

  const storageKey = `${STORAGE_PREFIX}${projectId}`;

  // Restore draft from sessionStorage on mount
  useEffect(() => {
    if (!projectId) return;
    try {
      const saved = sessionStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
          setDraftChanges(parsed);
        }
      }
    } catch {
      // Ignore parse errors
    }
  }, [storageKey, projectId]);

  // Persist draft to sessionStorage whenever it changes
  useEffect(() => {
    if (!projectId) return;
    if (Object.keys(draftChanges).length > 0) {
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(draftChanges));
      } catch {
        // Storage full — ignore
      }
    } else {
      sessionStorage.removeItem(storageKey);
    }
  }, [draftChanges, projectId, storageKey]);

  const hasChanges = Object.keys(draftChanges).length > 0;

  // Warn before tab close when there are unsaved changes
  useEffect(() => {
    if (!hasChanges) return;

    const handler = e => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasChanges]);

  // O(1) lookup map for tableData by master_item_id
  const tableDataMap = useMemo(() => {
    const map = {};
    if (!tableData) return map;
    tableData.forEach(i => {
      map[i.master_item_id] = i;
    });
    return map;
  }, [tableData]);

  // Count project items per master_item_id, split by quoted/unquoted
  const itemCounts = useMemo(() => {
    const counts = {};
    if (!projectItems) return counts;
    projectItems.forEach(pi => {
      if (!pi.master_item_id) return;
      if (!counts[pi.master_item_id]) {
        counts[pi.master_item_id] = { total: 0, quoted: 0 };
      }
      counts[pi.master_item_id].total += 1;
      if (pi.active_quote_line_id) {
        counts[pi.master_item_id].quoted += 1;
      }
    });
    return counts;
  }, [projectItems]);

  /**
   * Buffer an option change (does NOT write to DB).
   * Auto-fills cost from option.default_cost if user hasn't manually set a cost.
   *
   * @param {string} masterItemId
   * @param {string} group - The option group key (e.g., 'default', 'פרופיל', 'selected_child_id')
   * @param {string} value - The selected option name or child ID
   * @param {Object} masterItem - The raw master item data (for looking up default_cost)
   */
  const bufferOptionChange = useCallback(
    (masterItemId, group, value, masterItem) => {
      const item = tableDataMap[masterItemId];
      if (!item) return;

      setDraftChanges(prev => {
        const existing = prev[masterItemId] || {};
        const currentOption = existing.selectedOption ?? item.selected_option ?? {};
        const originalOption = existing.originalOption ?? item.selected_option ?? {};
        const originalCost = existing.originalCost ?? item.selected_option?.cost ?? null;

        const newSelectedOption = { ...currentOption, [group]: value };

        // Auto-fill cost from option's default_cost (only if user hasn't manually set a cost)
        let newCost = existing.cost;
        if (!existing.costManuallySet && masterItem?.item_options?.options) {
          for (const opt of masterItem.item_options.options) {
            const optGroup = opt.group || 'default';
            if (optGroup === group && opt.name === value && opt.default_cost != null) {
              newCost = opt.default_cost;
              break;
            }
          }
        }

        // For choice bundles, auto-fill from child's estimated_cost
        if (group === 'selected_child_id' && !existing.costManuallySet) {
          const child = item.bundle_children?.find(c => c.id === value);
          if (child?.estimated_cost != null) {
            newCost = child.estimated_cost;
          }
        }

        return {
          ...prev,
          [masterItemId]: {
            ...existing,
            selectedOption: newSelectedOption,
            originalOption,
            originalCost,
            cost: newCost,
          },
        };
      });
    },
    [tableDataMap],
  );

  /**
   * Buffer a cost change (does NOT write to DB).
   * Marks cost as manually set so option auto-fill won't override it.
   */
  const bufferCostChange = useCallback(
    (masterItemId, cost) => {
      const item = tableDataMap[masterItemId];
      if (!item) return;

      setDraftChanges(prev => {
        const existing = prev[masterItemId] || {};
        const originalOption = existing.originalOption ?? item.selected_option ?? {};
        const originalCost = existing.originalCost ?? item.selected_option?.cost ?? null;

        return {
          ...prev,
          [masterItemId]: {
            ...existing,
            selectedOption: existing.selectedOption ?? item.selected_option ?? {},
            originalOption,
            originalCost,
            cost: cost ? parseFloat(cost) : null,
            costManuallySet: true,
          },
        };
      });
    },
    [tableDataMap],
  );

  /**
   * Get the effective selected_option for an item, merging draft on top of saved.
   */
  const getEffectiveOption = useCallback(
    masterItemId => {
      const draft = draftChanges[masterItemId];
      if (!draft) return null;
      return draft.selectedOption || null;
    },
    [draftChanges],
  );

  /**
   * Get the effective cost for an item (draft cost or original).
   */
  const getEffectiveCost = useCallback(
    masterItemId => {
      const draft = draftChanges[masterItemId];
      if (!draft) return undefined;
      if (draft.cost !== undefined) return draft.cost;
      return draft.selectedOption?.cost ?? undefined;
    },
    [draftChanges],
  );

  /**
   * Compute deltas per master_item_id.
   * Returns: { [master_item_id]: { costDelta, perItemDelta, itemCount, quotedCount } }
   */
  const deltas = useMemo(() => {
    const result = {};
    if (Object.keys(draftChanges).length === 0) return result;

    Object.entries(draftChanges).forEach(([masterItemId, draft]) => {
      const originalCost = draft.originalCost ?? 0;
      const newCost =
        draft.cost !== undefined && draft.cost !== null
          ? draft.cost
          : (draft.selectedOption?.cost ?? originalCost);

      const costDiff = newCost - originalCost;
      const counts = itemCounts[masterItemId] || { total: 0, quoted: 0 };

      if (costDiff !== 0 || hasOptionChanged(draft)) {
        result[masterItemId] = {
          costDelta: costDiff * counts.total,
          perItemDelta: costDiff,
          itemCount: counts.total,
          quotedCount: counts.quoted,
        };
      }
    });

    return result;
  }, [draftChanges, itemCounts]);

  /**
   * Aggregate totals across all changed items.
   */
  const totals = useMemo(() => {
    const entries = Object.values(deltas);
    if (entries.length === 0) {
      return { totalDelta: 0, totalItems: 0, totalQuoted: 0, changeCount: 0 };
    }
    return entries.reduce(
      (acc, d) => ({
        totalDelta: acc.totalDelta + d.costDelta,
        totalItems: acc.totalItems + d.itemCount,
        totalQuoted: acc.totalQuoted + d.quotedCount,
        changeCount: acc.changeCount + 1,
      }),
      { totalDelta: 0, totalItems: 0, totalQuoted: 0, changeCount: 0 },
    );
  }, [deltas]);

  const budgetPercentage = totalBudget
    ? ((Math.abs(totals.totalDelta) / totalBudget) * 100).toFixed(1)
    : null;

  /**
   * Build summary list for the confirmation modal.
   */
  const changeSummary = useMemo(() => {
    if (!tableData) return [];
    return Object.entries(draftChanges)
      .map(([masterItemId, draft]) => {
        const item = tableDataMap[masterItemId];
        if (!item) return null;

        const delta = deltas[masterItemId];
        if (!delta && !hasOptionChanged(draft)) return null;

        return {
          masterItemId,
          itemName: item.item_name,
          oldOption: draft.originalOption,
          newOption: draft.selectedOption,
          oldCost: draft.originalCost,
          newCost:
            draft.cost !== undefined && draft.cost !== null
              ? draft.cost
              : (draft.selectedOption?.cost ?? draft.originalCost),
          costDelta: delta?.costDelta ?? 0,
          itemCount: delta?.itemCount ?? 0,
          quotedCount: delta?.quotedCount ?? 0,
        };
      })
      .filter(Boolean);
  }, [draftChanges, deltas, tableData, tableDataMap]);

  const discard = useCallback(() => {
    setDraftChanges({});
    sessionStorage.removeItem(storageKey);
  }, [storageKey]);

  return {
    hasChanges,
    draftChanges,
    bufferOptionChange,
    bufferCostChange,
    getEffectiveOption,
    getEffectiveCost,
    deltas,
    totals,
    budgetPercentage,
    changeSummary,
    discard,
    itemCounts,
  };
}

/** Check if options (excluding cost/notes) actually changed */
function hasOptionChanged(draft) {
  if (!draft.originalOption && !draft.selectedOption) return false;
  const orig = { ...draft.originalOption };
  const next = { ...draft.selectedOption };
  delete orig.cost;
  delete orig.notes;
  delete next.cost;
  delete next.notes;
  return JSON.stringify(orig) !== JSON.stringify(next);
}
